import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { trustedJsonLd } from '@app/core/security/trusted-types';

export type OgType = 'website' | 'article' | 'profile' | 'book' | string;
export type TwitterCard = 'summary' | 'summary_large_image' | 'app' | 'player';

export interface SeoData {
  title?: string;
  description?: string;
  canonicalPath?: string;
  robots?: string;
  appendAppName?: boolean;

  ogTitle?: string;
  ogDescription?: string;
  ogType?: OgType;
  ogImage?: string;

  twitterCard?: TwitterCard;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;

  jsonLd?: Record<string, any> | Record<string, any>[];
}

const DEFAULT_DESCRIPTION =
  'Centelha é o lugar pra cultivar reflexões espirituais, ler meditações diárias e dialogar com sua fé no dia a dia.';
const DEFAULT_ROBOTS = 'index, follow';
const DEFAULT_OG_IMAGE = '/assets/logo/logo-full.png';
const DEFAULT_OG_TYPE: OgType = 'website';
const DEFAULT_TWITTER_CARD: TwitterCard = 'summary_large_image';
const SITE_NAME = 'Centelha';
const LOCALE = 'pt_BR';
const JSON_LD_ID = 'seo-jsonld-route';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  apply(data: SeoData): void {
    this.applyTitle(data);
    this.applyDescription(data);
    this.applyRobots(data);
    this.applyCanonical(data);
    this.applyOpenGraph(data);
    this.applyTwitter(data);
    this.applyJsonLd(data);
  }

  reset(): void {
    this.apply({});
  }

  private applyTitle(data: SeoData): void {
    const appName = environment.APP_NAME;
    const workspacePrefix = this.workspacePrefix();
    const base = data.title?.trim();
    const appendAppName = data.appendAppName ?? true;

    let composed: string;
    if (!base) {
      composed = appName;
    } else if (appendAppName && !base.toLowerCase().includes(appName.toLowerCase())) {
      composed = `${base} | ${appName}`;
    } else {
      composed = base;
    }

    const finalTitle = `${workspacePrefix}${composed}`;
    this.title.setTitle(finalTitle);
    this.meta.updateTag({ property: 'og:title', content: data.ogTitle?.trim() || composed });
    this.meta.updateTag({ name: 'twitter:title', content: data.twitterTitle?.trim() || composed });
  }

  private applyDescription(data: SeoData): void {
    const description = data.description?.trim() || DEFAULT_DESCRIPTION;
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: data.ogDescription?.trim() || description });
    this.meta.updateTag({ name: 'twitter:description', content: data.twitterDescription?.trim() || description });
  }

  private applyRobots(data: SeoData): void {
    const robots = data.robots?.trim() || DEFAULT_ROBOTS;
    this.meta.updateTag({ name: 'robots', content: robots });
  }

  private applyCanonical(data: SeoData): void {
    const origin = this.resolveOrigin();
    if (!origin) return;

    const path = data.canonicalPath ?? this.currentPath();
    const url = this.buildAbsoluteUrl(origin, path);

    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);

    this.meta.updateTag({ property: 'og:url', content: url });
  }

  private applyOpenGraph(data: SeoData): void {
    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ property: 'og:locale', content: LOCALE });
    this.meta.updateTag({ property: 'og:type', content: data.ogType || DEFAULT_OG_TYPE });

    const image = this.resolveImageUrl(data.ogImage || DEFAULT_OG_IMAGE);
    if (image) {
      this.meta.updateTag({ property: 'og:image', content: image });
      this.meta.updateTag({ property: 'og:image:alt', content: data.ogTitle || data.title || SITE_NAME });
    }
  }

  private applyTwitter(data: SeoData): void {
    this.meta.updateTag({ name: 'twitter:card', content: data.twitterCard || DEFAULT_TWITTER_CARD });

    const image = this.resolveImageUrl(data.twitterImage || data.ogImage || DEFAULT_OG_IMAGE);
    if (image) {
      this.meta.updateTag({ name: 'twitter:image', content: image });
    }
  }

  private applyJsonLd(data: SeoData): void {
    this.removeJsonLd();
    if (!data.jsonLd) return;

    const items = Array.isArray(data.jsonLd) ? data.jsonLd : [data.jsonLd];
    items.forEach((item, index) => {
      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `${JSON_LD_ID}-${index}`;
      script.text = trustedJsonLd(JSON.stringify(item)) as string;
      this.document.head.appendChild(script);
    });
  }

  private removeJsonLd(): void {
    const nodes = this.document.querySelectorAll(`script[id^="${JSON_LD_ID}"]`);
    nodes.forEach((node) => node.parentNode?.removeChild(node));
  }

  private workspacePrefix(): string {
    const prefixes: Record<string, string> = {
      sandbox: '[Sandbox] ',
      local: '[Local] ',
      prod: '',
    };
    return prefixes[environment.WORKSPACE] ?? '';
  }

  private resolveOrigin(): string | null {
    const fromEnv = (environment as any).APP_URL as string | undefined;
    if (fromEnv) return fromEnv.replace(/\/$/, '');
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return null;
  }

  private currentPath(): string {
    if (typeof window === 'undefined') return '/';
    const { pathname, search } = window.location;
    return `${pathname}${search}`;
  }

  private buildAbsoluteUrl(origin: string, path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${normalized}`;
  }

  private resolveImageUrl(path?: string): string | null {
    if (!path) return null;
    const origin = this.resolveOrigin();
    if (!origin) return path;
    return this.buildAbsoluteUrl(origin, path);
  }
}
